import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ReadOnlyBanner } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Search, Pencil, Trash2, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function History() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const readOnly = user?.subscription_status === "read_only";
  const [entries, setEntries] = useState([]);
  const [search, setSearch] = useState("");
  const [range, setRange] = useState({ start: "", end: "" });
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const params = {};
    if (search) params.search = search;
    if (range.start && range.end) { params.start_date = range.start; params.end_date = range.end; }
    try {
      const res = await api.get("/logs", { params });
      setEntries(res.data);
    } catch (err) { toast.error(apiError(err)); }
  }, [search, range]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const saveEdit = async () => {
    try {
      await api.put(`/logs/${editing.id}`, {
        activity: editing.activity, date: editing.date,
        hours: parseInt(editing.hours) || 0, minutes: parseInt(editing.minutes) || 0,
        linked_objective: editing.linked_objective, notes: editing.notes,
      });
      toast.success(t("history.updated"));
      setEditing(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/logs/${id}`); toast.success(t("history.deleted")); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const fmtTime = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return ""; } };

  return (
    <div className="animate-fade-up">
      <ReadOnlyBanner />
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold text-navy">{t("history.title")}</h1>
        <p className="text-slatebody mt-1">{t("history.subtitle")}</p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 mb-6 flex flex-col md:flex-row gap-3 md:items-end">
        <div className="flex-1">
          <Label className="text-xs">{t("common.search")}</Label>
          <div className="relative mt-1.5">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slatebody" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" placeholder={t("history.searchPlaceholder")} data-testid="history-search" />
          </div>
        </div>
        <div>
          <Label className="text-xs">{t("common.from")}</Label>
          <Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="mt-1.5 font-mono" data-testid="history-start" />
        </div>
        <div>
          <Label className="text-xs">{t("common.to")}</Label>
          <Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="mt-1.5 font-mono" data-testid="history-end" />
        </div>
        {(range.start || range.end || search) && (
          <Button variant="outline" onClick={() => { setSearch(""); setRange({ start: "", end: "" }); }} data-testid="history-clear">{t("history.clear")}</Button>
        )}
      </div>

      <div className="space-y-3" data-testid="history-list">
        {entries.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center">
            <p className="text-slatebody">{t("history.noEntries")}</p>
          </div>
        )}
        {entries.map((e) => (
          <div key={e.id} className="rounded-xl border border-slate-200 bg-white p-5 flex items-start gap-4 hover:border-slate-300 transition-colors" data-testid={`history-item-${e.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1.5">
                <span className="font-mono text-xs font-medium text-[#202123]">{e.date}</span>
                <span className="font-mono text-[11px] text-slatebody flex items-center gap-1"><Clock className="h-3 w-3" />{e.hours || 0}h {e.minutes || 0}m</span>
                {e.linked_objective && <span className="text-[11px] rounded-full bg-accent px-2 py-0.5 text-accent-foreground">{e.linked_objective}</span>}
              </div>
              <p className="text-navy leading-snug">{e.activity}</p>
              {e.notes && <p className="text-sm text-slatebody mt-1.5">{e.notes}</p>}
              <p className="font-mono text-[10px] text-slate-400 mt-2">{t("history.loggedAt")} {fmtTime(e.created_at)}</p>
            </div>
            <div className="flex gap-1 shrink-0">
              <button onClick={() => setEditing({ ...e, hours: String(e.hours || 0), minutes: String(e.minutes || 0), notes: e.notes || "", linked_objective: e.linked_objective || "" })}
                disabled={readOnly} className="p-2 rounded-md hover:bg-background text-slatebody hover:text-navy transition-colors disabled:opacity-40" data-testid={`edit-${e.id}`}>
                <Pencil className="h-4 w-4" />
              </button>
              <button onClick={() => remove(e.id)} className="p-2 rounded-md hover:bg-red-50 text-slatebody hover:text-red-500 transition-colors" data-testid={`delete-${e.id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-white" data-testid="edit-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("history.editEntry")}</DialogTitle>
            <DialogDescription>{t("history.editDesc")}</DialogDescription></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label>{t("dailyLog.activity")}</Label>
                <Textarea value={editing.activity} onChange={(e) => setEditing({ ...editing, activity: e.target.value })} className="mt-1.5" rows={3} data-testid="edit-activity" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>{t("common.date")}</Label><Input type="date" value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} className="mt-1.5 font-mono" /></div>
                <div><Label>{t("history.hours")}</Label><Input type="number" min="0" value={editing.hours} onChange={(e) => setEditing({ ...editing, hours: e.target.value })} className="mt-1.5 font-mono" /></div>
                <div><Label>{t("history.minutes")}</Label><Input type="number" min="0" max="59" value={editing.minutes} onChange={(e) => setEditing({ ...editing, minutes: e.target.value })} className="mt-1.5 font-mono" /></div>
              </div>
              <div><Label>{t("dailyLog.notes")}</Label><Textarea value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} className="mt-1.5" rows={2} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>{t("common.cancel")}</Button>
            <Button className="bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0]" onClick={saveEdit} data-testid="edit-save">{t("history.saveChanges")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
